import {
  getAccountPayableKey,
  getAccountPayablesKey,
} from 'api-hooks/account-payable';
import {
  getAccountReceivableKey,
  getAccountReceivablesKey,
} from 'api-hooks/account-receivable';
import { getActivitiesKey, getActivityKey } from 'api-hooks/activity';
import { getAgentKey, getAgentsKey } from 'api-hooks/agent';
import { getAirlineKey, getAirlinesKey } from 'api-hooks/airline';
import {
  getApprovalMasterKey,
  getApprovalMastersKey,
} from 'api-hooks/approval-master';
import { getBankAccountKey, getBankAccountsKey } from 'api-hooks/bank-account';
import { bonusPeriodKeys } from 'api-hooks/bonus/query';
import { getCashAccountKey, getCashAccountsKey } from 'api-hooks/cash-account';
import { getChequeGiroKey, getChequeGirosKey } from 'api-hooks/cheque-giro';
import { getCommissionKey, getCommissionsKey } from 'api-hooks/commission';
import { getCruiseKey, getCruisesKey } from 'api-hooks/cruise';
import { getCustomerKey, getCustomersKey } from 'api-hooks/customer';
import { getEmployeeKey, getEmployeesKey } from 'api-hooks/employee';
import {
  ApprovableType,
  CompleteableType,
  DestroyableType,
  ResolveableType,
  RevertVoidableType,
  SalesActionsType,
  VoidableType,
} from 'api-hooks/general/model';
import { getGuestKey, getGuestsKey } from 'api-hooks/guest';
import { getHotelKey, getHotelsKey } from 'api-hooks/hotel';
import {
  getActivityPurchaseKey,
  getActivityPurchasesKey,
} from 'api-hooks/order/activity';
import {
  getCruisePurchaseKey,
  getCruisePurchasesKey,
} from 'api-hooks/order/cruise';
import {
  getHotelPurchaseKey,
  getHotelPurchasesKey,
} from 'api-hooks/order/hotel';
import {
  getFlightPurchaseKey,
  getFlightPurchasesKey,
} from 'api-hooks/order/ticket-order';
import {
  getDocumentPurchaseKey,
  getDocumentPurchasesKey,
} from 'api-hooks/order/travel-document';
import {
  getOrderAdjustmentKey,
  getOrderAdjustmentsKey,
} from 'api-hooks/order-adjustment';
import {
  getPaymentChannelKey,
  getPaymentChannelsKey,
} from 'api-hooks/payment-channel';
import { getPreferenceGroupsKey } from 'api-hooks/preference-tag';
import {
  getProductAdjustmentKey,
  getProductsAdjustmentKey,
} from 'api-hooks/product/adjustment/query';
import { getProductListKey, getProductsListKey } from 'api-hooks/product/list';
import {
  getProductPurchaseKey,
  getProductsPurchaseKey,
} from 'api-hooks/product/order';
import { getProductSaleKey, getProductsSaleKey } from 'api-hooks/product/sales';
import { getProjectKey, getProjectsKey } from 'api-hooks/projects';
import { getReceiptKey, getReceiptsKey } from 'api-hooks/receipt';
import { getRefundKey, getRefundsKey } from 'api-hooks/refund/refund';
import {
  getRefundReleaseAgentKey,
  getRefundReleaseAgentsKey,
} from 'api-hooks/refund/refund-release-agent';
import {
  getRefundReleaseUserKey,
  getRefundReleaseUsersKey,
} from 'api-hooks/refund/refund-release-user';
import { getRoleKey, getRolesKey } from 'api-hooks/role';
import {
  getActivitySaleKey,
  getActivitySalesKey,
} from 'api-hooks/sale/activity';
import { getCruiseSaleKey, getCruiseSalesKey } from 'api-hooks/sale/cruise';
import { getHotelSaleKey, getHotelSalesKey } from 'api-hooks/sale/hotel';
import {
  getTicketSaleKey,
  getTicketSalesKey,
} from 'api-hooks/sale/ticket-sale';
import {
  getTravelDocumentSalesKey,
  getTravelDocumentsSalesKey,
} from 'api-hooks/sale/travel-document';
import { getTourKey, getToursKey } from 'api-hooks/tour/tour-list/tour-detail';
import { getTourSaleKey, getTourSalesKey } from 'api-hooks/tour/tour-sale';
import { transactionCategoryKeys } from 'api-hooks/transaction-category';
import {
  getBalanceAdjustmentKey,
  getBalanceAdjustmentsKey,
} from 'api-hooks/transactions/balance-adjustment';
import {
  getBankTransactionKey,
  getBankTransactionsKey,
} from 'api-hooks/transactions/bank-transaction';
import {
  getCashTransactionKey,
  getCashTransactionsKey,
} from 'api-hooks/transactions/cash-transaction';
import {
  getTransferKey,
  getTransfersKey,
} from 'api-hooks/transactions/transfer';
import { visaDocumentKeys } from 'api-hooks/visa-document';
import { queryClient } from 'common/repositories/query-client';
import React from 'react';

export default function useDismiss(
  type?:
    | ApprovableType
    | VoidableType
    | RevertVoidableType
    | ResolveableType
    | CompleteableType
    | DestroyableType
    | SalesActionsType,
  id?: string,
) {
  const onDismiss = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? id;
      const input = _id ? { id: _id } : undefined;

      switch (type) {
        case 'account-payables':
          queryClient.refetchQueries([getAccountPayablesKey()[0]]);
          input && queryClient.refetchQueries(getAccountPayableKey(input));
          break;
        case 'account-receivables':
          queryClient.refetchQueries([getAccountReceivablesKey()[0]]);
          input && queryClient.refetchQueries(getAccountReceivableKey(input));
          break;
        case 'activity-purchases':
          queryClient.refetchQueries([getActivityPurchasesKey()[0]]);
          input && queryClient.refetchQueries(getActivityPurchaseKey(input));
          break;
        case 'activity-sales':
          queryClient.refetchQueries([getActivitySalesKey()[0]]);
          input && queryClient.refetchQueries(getActivitySaleKey(input));
          break;
        case 'balance-adjustments':
          queryClient.refetchQueries([getBalanceAdjustmentsKey()[0]]);
          input && queryClient.refetchQueries(getBalanceAdjustmentKey(input));
          break;
        case 'bank-transactions':
          queryClient.refetchQueries([getBankTransactionsKey()[0]]);
          input && queryClient.refetchQueries(getBankTransactionKey(input));
          break;
        case 'cash-transactions':
          queryClient.refetchQueries([getCashTransactionsKey()[0]]);
          input && queryClient.refetchQueries(getCashTransactionKey(input));
          break;
        case 'cruise-purchases':
          queryClient.refetchQueries([getCruisePurchasesKey()[0]]);
          input && queryClient.refetchQueries(getCruisePurchaseKey(input));
          break;
        case 'cruise-sales':
          queryClient.refetchQueries([getCruiseSalesKey()[0]]);
          input && queryClient.refetchQueries(getCruiseSaleKey(input));
          break;
        case 'document-purchases':
          queryClient.refetchQueries([getDocumentPurchasesKey()[0]]);
          input && queryClient.refetchQueries(getDocumentPurchaseKey(input));
          break;
        case 'document-sales':
          queryClient.refetchQueries([getTravelDocumentsSalesKey()[0]]);
          input && queryClient.refetchQueries(getTravelDocumentSalesKey(input));
          break;
        case 'flight-purchases':
          queryClient.refetchQueries([getFlightPurchasesKey()[0]]);
          input && queryClient.refetchQueries(getFlightPurchaseKey(input));
          break;
        case 'flight-sales':
          queryClient.refetchQueries([getTicketSalesKey()[0]]);
          input && queryClient.refetchQueries(getTicketSaleKey(input));
          break;
        case 'hotel-purchases':
          queryClient.refetchQueries([getHotelPurchasesKey()[0]]);
          input && queryClient.refetchQueries(getHotelPurchaseKey(input));
          break;
        case 'hotel-sales':
          queryClient.refetchQueries([getHotelSalesKey()[0]]);
          input && queryClient.refetchQueries(getHotelSaleKey(input));
          break;
        case 'order-adjustments':
          queryClient.refetchQueries([getOrderAdjustmentsKey()[0]]);
          input && queryClient.refetchQueries(getOrderAdjustmentKey(input));
          break;
        case 'other-expenses':
          queryClient.refetchQueries(['get-other-expenses-tour']);
          queryClient.refetchQueries(['get-other-expenses-sales']);
          break;
        case 'other-incomes':
          queryClient.refetchQueries(['get-other-incomes-tour']);
          queryClient.refetchQueries(['get-other-incomes-sales']);
          break;
        case 'product-purchases':
          queryClient.refetchQueries([getProductsPurchaseKey()[0]]);
          input && queryClient.refetchQueries(getProductPurchaseKey(input));
          break;
        case 'product-sales':
          queryClient.refetchQueries([getProductsSaleKey()[0]]);
          input && queryClient.refetchQueries(getProductSaleKey(input));
          break;
        case 'product-adjustments':
          queryClient.refetchQueries([getProductsAdjustmentKey()[0]]);
          input && queryClient.refetchQueries(getProductAdjustmentKey(input));
          break;
        case 'receipts':
          queryClient.refetchQueries([getReceiptsKey()[0]]);
          input && queryClient.refetchQueries(getReceiptKey(input));
          break;
        case 'tour-sales':
          queryClient.refetchQueries([getTourSalesKey()[0]]);
          input && queryClient.refetchQueries(getTourSaleKey(input));
          break;
        case 'transfers':
          queryClient.refetchQueries([getTransfersKey()[0]]);
          input && queryClient.refetchQueries(getTransferKey(input));
          break;
        case 'refunds':
          queryClient.refetchQueries([getRefundsKey()[0]]);
          input && queryClient.refetchQueries(getRefundKey(input));
          break;
        case 'release-purchases':
          queryClient.refetchQueries([getRefundReleaseAgentsKey()[0]]);
          input && queryClient.refetchQueries(getRefundReleaseAgentKey(input));
          break;
        case 'release-sales':
          queryClient.refetchQueries([getRefundReleaseUsersKey()[0]]);
          input && queryClient.refetchQueries(getRefundReleaseUserKey(input));
          break;
        case 'projects':
          queryClient.refetchQueries([getProjectsKey()[0]]);
          input && queryClient.refetchQueries(getProjectKey(input));
          break;
        case 'activities':
          queryClient.refetchQueries([getActivitiesKey()[0]]);
          input && queryClient.refetchQueries(getActivityKey(input));
          break;
        case 'agents':
          queryClient.refetchQueries([getAgentsKey()[0]]);
          input && queryClient.refetchQueries(getAgentKey(input));
          break;
        case 'airlines':
          queryClient.refetchQueries([getAirlinesKey()[0]]);
          input && queryClient.refetchQueries(getAirlineKey(input));
          break;
        case 'approvals':
          queryClient.refetchQueries([getApprovalMastersKey()[0]]);
          input && queryClient.refetchQueries(getApprovalMasterKey(input));
          break;
        case 'bank-accounts':
          queryClient.refetchQueries([getBankAccountsKey()[0]]);
          input && queryClient.refetchQueries(getBankAccountKey(input));
          break;
        case 'cash-accounts':
          queryClient.refetchQueries([getCashAccountsKey()[0]]);
          input && queryClient.refetchQueries(getCashAccountKey(input));
          break;
        case 'cheque-giros':
          queryClient.refetchQueries([getChequeGirosKey()[0]]);
          input && queryClient.refetchQueries(getChequeGiroKey(input));
          break;
        case 'cruises':
          queryClient.refetchQueries([getCruisesKey()[0]]);
          input && queryClient.refetchQueries(getCruiseKey(input));
          break;
        case 'customers':
          queryClient.refetchQueries([getCustomersKey()[0]]);
          input && queryClient.refetchQueries(getCustomerKey(input));
          break;
        case 'employees':
          queryClient.refetchQueries([getEmployeesKey()[0]]);
          input && queryClient.refetchQueries(getEmployeeKey(input));
          break;
        case 'guests':
          queryClient.refetchQueries([getGuestsKey()[0]]);
          input && queryClient.refetchQueries(getGuestKey(input));
          break;
        case 'hotels':
          queryClient.refetchQueries([getHotelsKey()[0]]);
          input && queryClient.refetchQueries(getHotelKey(input));
          break;
        case 'payment-channels':
          queryClient.refetchQueries([getPaymentChannelsKey()[0]]);
          input && queryClient.refetchQueries(getPaymentChannelKey(input));
          break;
        case 'preference-groups':
          queryClient.refetchQueries([getPreferenceGroupsKey()[0]]);
          break;
        case 'products':
          queryClient.refetchQueries([getProductsListKey()[0]]);
          input && queryClient.refetchQueries(getProductListKey(input));
          break;
        case 'roles':
          queryClient.refetchQueries([getRolesKey()[0]]);
          input && queryClient.refetchQueries(getRoleKey(input));
          break;
        case 'transaction-categories':
          queryClient.refetchQueries([transactionCategoryKeys.index()[0]]);
          input &&
            queryClient.refetchQueries(transactionCategoryKeys.view(input));
          break;
        case 'visa-documents':
          queryClient.refetchQueries([visaDocumentKeys.index()[0]]);
          input && queryClient.refetchQueries(visaDocumentKeys.view(input));
          break;
        case 'tours':
          queryClient.refetchQueries([getToursKey()[0]]);
          input && queryClient.refetchQueries(getTourKey(input));
          break;
        case 'commission-masters':
          queryClient.refetchQueries([getCommissionsKey()[0]]);
          input && queryClient.refetchQueries(getCommissionKey(input));
          break;
        case 'bonus-periods':
          queryClient.refetchQueries([bonusPeriodKeys.index()[0]]);
          input && queryClient.refetchQueries(bonusPeriodKeys.view(input));
          break;
        case 'tour-categories':
        default:
          queryClient.refetchQueries();
          break;
      }
    },
    [id, type],
  );
  return onDismiss;
}
